import { Control, FieldValues, useWatch } from "react-hook-form";
import { SectionHeader } from "./SectionHeader";
import { useDniValidationStore } from "@/store/dniValidationStore";
import { Button, Flex, Text } from "@chakra-ui/react";
import { ArrowRight, Plus, SkipBack, SkipForward, X } from "lucide-react";
import { AppColors } from "@/styles/colors";
import { useEmailValidationStore } from "@/store/emailValidationStore";

interface Props {
  handleAdd: (omitStep?: boolean) => void;
  handleCancel: () => void;
  handleBack?: () => void;
  control: Control<FieldValues>;
  currentStep?: number;
  loading?: boolean;
  addLabel?: string;
  title: string;
  steps?: number;
  operation?: "CREATE" | "UPDATE";
}

export const PersonAddCancelButtons = ({
  handleAdd,
  handleCancel,
  handleBack,
  control,
  currentStep,
  loading,
  addLabel,
  steps,
  title,
  operation = "CREATE",
}: Props) => {
  const [dni, nombre, apellido, cuenta] = useWatch({
    control,
    name: ["dni", "nombre", "apellido", "cuenta"],
  });

  const { isValid: isValidDni } = useDniValidationStore();
  const { isValid: isValidEmail } = useEmailValidationStore();

  const disableAdd =
    !dni ||
    !nombre ||
    !apellido ||
    (!cuenta && operation === "CREATE" && currentStep === 1) ||
    !isValidEmail ||
    !isValidDni;

  const stepLabel =
    currentStep && currentStep >= Number(steps) ? "Finalizar" : "Continuar";
  const addLabelText = typeof currentStep === "number" ? stepLabel : addLabel;

  return (
    <SectionHeader
      showButtons
      title={title}
      loading={loading}
      aditionalButtons={
        <Flex gap="16px">
          <Button
            paddingX="12px"
            height="32px"
            bgColor={AppColors.GREEN_PRIMARY}
            data-testid="sectionheader-add-button"
            onClick={() => handleAdd()}
            disabled={disableAdd}
          >
            {stepLabel === "Continuar" ? <ArrowRight /> : <Plus />}
            <Text fontWeight={600} fontSize="14px">
              {addLabelText}
            </Text>
          </Button>
          {Boolean(currentStep && currentStep > 0) && (
            <Button
              paddingX="12px"
              height="32px"
              onClick={handleBack}
              data-testid="sectionheader-back-button"
            >
              <SkipBack />
              <Text fontWeight={600} fontSize="14px">
                Volver
              </Text>
            </Button>
          )}

          {Boolean(currentStep && currentStep > 0) && (
            <Button
              paddingX="12px"
              height="32px"
              onClick={() => handleAdd(true)}
              data-testid="sectionheader-omit-button"
            >
              <SkipForward />
              <Text fontWeight={600} fontSize="14px">
                {currentStep === steps ? "Omitir y finalizar" : "Omitir"}
              </Text>
            </Button>
          )}
          <Button
            paddingX="12px"
            height="32px"
            onClick={handleCancel}
            data-testid="sectionheader-cancel-button"
          >
            <X />
            <Text fontWeight={600} fontSize="14px">
              Cancelar
            </Text>
          </Button>
        </Flex>
      }
    />
  );
};
